import React, { useState, useEffect } from 'react';
import api from '../api/api';
import moment from 'moment';
import ClearRoundedIcon from '@mui/icons-material/ClearRounded';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import InboxRoundedIcon from '@mui/icons-material/InboxRounded';
import { CircularProgress } from '@mui/material';

// Human-readable labels for the fields inside an EDIT request payload
const FIELD_LABELS = {
  firstname: 'Firstname',
  lastname: 'Lastname',
  username: 'Username',
  password: 'Password',
  mpin: 'PIN',
  phone: 'Phone',
  email: 'Email',
  gender: 'Gender',
  status: 'Status',
  role: 'Role',
  ipadid: "iPad's",
  user_info: 'Company / Permissions',
};

const getInitials = (name = '') => {
  const parts = name.trim().split(/\s+/);
  return ((parts[0]?.[0] || '') + (parts[1]?.[0] || '')).toUpperCase() || '?';
};

const ApprovalRequestsModal = ({ isOpen, onClose, onActionDone, showToast }) => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [processingId, setProcessingId] = useState(null);

  const fetchRequests = async () => {
    setLoading(true);
    try {
      const response = await api.get('/v1/user-approvals?status=P');
      setRequests(response.data || []);
    } catch (error) {
      console.error('Failed to fetch approval requests:', error);
      showToast('Failed to load approval requests', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) fetchRequests();
  }, [isOpen]);

  const handleReview = async (id, action) => {
    setProcessingId(id);
    try {
      const response = await api.put(`/v1/user-approvals/${id}`, { action });
      showToast(response.message || `Request ${action}d`, action === 'approve' ? 'success' : 'info');
      await fetchRequests();
      onActionDone && onActionDone();
    } catch (error) {
      console.error(`Failed to ${action} request:`, error);
      showToast(error.response?.data?.message || `Failed to ${action} request`, 'error');
    } finally {
      setProcessingId(null);
    }
  };

  const changedFields = (payload) =>
    payload ? Object.keys(payload).map((key) => FIELD_LABELS[key] || key) : [];

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-start justify-center overflow-y-auto px-4">
      <div className="bg-white rounded-xl shadow-lg w-full max-w-2xl my-12 text-left">

        {/* Header */}
        <div className="flex items-start justify-between px-6 pt-5 pb-4 border-b">
          <div className="text-left">
            <h2 className="text-lg font-inter font-semibold text-gray-900 text-left m-0">
              Pending Approvals
            </h2>
            <p className="text-[13px] text-gray-500 font-inter text-left mt-0.5 mb-0">
              Delete requests waiting for your approval
            </p>
          </div>
          <button
            className="text-gray-400 hover:text-gray-700 -mr-1"
            onClick={onClose}
          >
            <ClearRoundedIcon />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-4">
          {loading ? (
            <div className="flex justify-center py-12">
              <CircularProgress size={30} />
            </div>
          ) : requests.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-gray-400">
              <InboxRoundedIcon style={{ fontSize: 40 }} />
              <p className="font-inter text-sm mt-2 mb-0">No pending requests</p>
              <p className="font-inter text-xs text-gray-300 mt-1 mb-0">
                You're all caught up
              </p>
            </div>
          ) : (
            <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-1">
              {requests.map((req) => {
                const isDelete = req.request_type === 'DELETE';
                const fields = changedFields(req.payload);
                return (
                  <div
                    key={req.id}
                    className="border border-gray-200 rounded-lg p-4 flex items-center gap-4 hover:border-gray-300 transition-colors"
                  >
                    {/* Avatar */}
                    <div
                      className={`h-10 w-10 shrink-0 rounded-full flex items-center justify-center text-sm font-semibold font-inter ${
                        isDelete ? 'bg-red-50 text-red-600' : 'bg-blue-50 text-blue-600'
                      }`}
                    >
                      {getInitials(req.target_user_name)}
                    </div>

                    {/* Details */}
                    <div className="flex-1 min-w-0 text-left">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-[14px] font-inter text-gray-900 truncate">
                          {req.target_user_name || `User #${req.target_user_id}`}
                        </span>
                        {isDelete ? (
                          <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide bg-red-50 text-red-600 border border-red-200 px-2 py-[2px] rounded-full">
                            <DeleteIcon style={{ fontSize: 11 }} /> Delete
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide bg-blue-50 text-blue-600 border border-blue-200 px-2 py-[2px] rounded-full">
                            <EditIcon style={{ fontSize: 11 }} /> Edit
                          </span>
                        )}
                      </div>

                      <p className="text-xs text-gray-500 font-inter mt-1 mb-0 text-left">
                        Requested by{' '}
                        <span className="font-medium text-gray-700">
                          {req.requested_by_name || '-'}
                        </span>
                        {req.created_at
                          ? ` · ${moment(req.created_at).format('MM-DD-YYYY hh:mm A')}`
                          : ''}
                      </p>

                      {!isDelete && fields.length > 0 && (
                        <div className="flex items-center gap-1.5 flex-wrap mt-2">
                          <span className="text-[11px] text-gray-400 font-inter">Changes:</span>
                          {fields.map((f) => (
                            <span
                              key={f}
                              className="text-[11px] font-inter bg-gray-100 text-gray-700 px-2 py-[2px] rounded-md"
                            >
                              {f}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-2 shrink-0">
                      <button
                        onClick={() => handleReview(req.id, 'approve')}
                        disabled={processingId === req.id}
                        className="inline-flex items-center gap-1 bg-green-600 hover:bg-green-700 text-white text-xs font-inter font-medium px-3 py-1.5 rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <CheckRoundedIcon style={{ fontSize: 15 }} /> Accept
                      </button>
                      <button
                        onClick={() => handleReview(req.id, 'reject')}
                        disabled={processingId === req.id}
                        className="inline-flex items-center gap-1 bg-white border border-gray-300 text-gray-700 hover:border-red-400 hover:text-red-600 text-xs font-inter font-medium px-3 py-1.5 rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <CloseRoundedIcon style={{ fontSize: 15 }} /> Reject
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        {!loading && requests.length > 0 && (
          <div className="px-6 py-3 border-t bg-gray-50 rounded-b-xl text-left">
            <p className="text-xs text-gray-500 font-inter m-0">
              {requests.length} pending request{requests.length > 1 ? 's' : ''}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ApprovalRequestsModal;
